function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useMemo } from 'react';
import { isAfter } from 'date-fns';
import { capitalize } from 'lodash-es';
import { Box, Column, extractPureTextFromRichText, Row, Typography } from 'seagull';
import { useResponsive } from "../../../../../../contexts";
import { useFeatureFlags } from "../../../../../../../hooks/useFeatureFlags";
import { useLocaleKeys } from "../../../../../../../i18n";
import { AgreementDataModel, serviceCostModel, serviceModel } from "../../../../../../../models";
import { ServiceCostType } from "../../../../../../../models/service-cost";
import { formatLongMonthDateWithLocalTimezone, utils } from "../../../../../../../utils";
import { getServiceBulletPoints } from "../../../../../../../utils/service-description/getServiceBulletPoints";
import { billingBadgesTextsPath, billingDetailsBadge, discountBadge as getDiscountBadge } from "../../../../../../../utils/service-description/getServiceDescriptionBadges";
import { DesktopServiceItem } from "./DesktopServiceItem";
import { MobileServiceItem } from "./MobileServiceItem";
import styles from "./ServiceItem.module.scss";
import { GetServiceItemTID } from "./ServiceItem.types";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const ServiceItem = _ref => {
  var _service$bundle;
  let {
    service,
    relationship,
    now,
    isOptional,
    isSelected,
    onToggle,
    testId
  } = _ref;
  const {
    ServiceItemTestIds
  } = useMemo(() => GetServiceItemTID(testId), [testId]);
  const featureFlags = useFeatureFlags();
  const {
    isMobile
  } = useResponsive();
  const {
    t: billingBadgesTexts
  } = useLocaleKeys(billingBadgesTextsPath);
  const agreementData = useMemo(() => AgreementDataModel.fromRelationship(relationship), [relationship]);
  const isDisabled = isOptional && !isSelected;
  const discountBadgeData = getDiscountBadge(service, billingBadgesTexts);
  const getBulletPoints = skin => getServiceBulletPoints({
    service,
    now,
    agreementData,
    isReadOnly: true,
    skin,
    variant: 'paragraph2',
    weight: 'light',
    featureFlags
  });
  const getBillingBadgeText = () => billingDetailsBadge(service, {
    t: billingBadgesTexts,
    effectiveDate: agreementData.effectiveDate,
    isClientPreview: true,
    now
  }).text;
  const getFutureBillingCallout = () => {
    const startBillingAt = serviceModel.startBillingAt(service);
    if (!startBillingAt) return null;
    if (isAfter(startBillingAt, now)) {
      return "The billing for this service is set to begin on ".concat(formatLongMonthDateWithLocalTimezone(startBillingAt));
    }
    return null;
  };
  const futureBillingText = getFutureBillingCallout();
  const renderPrice = () => {
    var _service$cost$discoun, _service$cost$discoun2;
    switch (service.cost.pricingType) {
      case ServiceCostType.Fixed:
      case ServiceCostType.Variable:
        return /*#__PURE__*/_jsxs(Row, {
          gap: 8,
          alignItems: "baseline",
          children: [/*#__PURE__*/_jsx(Typography, {
            variant: "h6",
            skin: isDisabled ? 'secondary' : 'standard',
            children: utils.formatDollarAmount(utils.applyDiscount(service.cost.price, service.cost.discount))
          }), ((_service$cost$discoun = service.cost.discount) === null || _service$cost$discoun === void 0 ? void 0 : _service$cost$discoun.amount()) && /*#__PURE__*/_jsx(Typography, {
            skin: "secondary",
            textDecoration: "line-through",
            children: utils.formatDollarAmount(service.cost.price)
          }), serviceCostModel.isVariablePrice(service.cost) && /*#__PURE__*/_jsxs(Typography, {
            variant: "paragraph2",
            skin: "secondary",
            children: ["/ ", capitalize(serviceCostModel.formattedUnitName(service.cost))]
          })]
        });
      case ServiceCostType.Range:
        return /*#__PURE__*/_jsxs(Row, {
          gap: 4,
          alignItems: "baseline",
          children: [/*#__PURE__*/_jsxs(Typography, {
            variant: "h6",
            skin: isDisabled ? 'secondary' : 'standard',
            children: [service.cost.minPrice && !service.cost.minPrice.eq(0) ? "".concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.minPrice, service.cost.discount)), " - ") : 'Up to ', utils.formatDollarAmount(utils.applyDiscount(service.cost.maxPrice, service.cost.discount))]
          }), ((_service$cost$discoun2 = service.cost.discount) === null || _service$cost$discoun2 === void 0 ? void 0 : _service$cost$discoun2.amount()) && /*#__PURE__*/_jsxs(Typography, {
            skin: "secondary",
            textDecoration: "line-through",
            children: [!!service.cost.minPrice && "".concat(utils.formatDollarAmount(service.cost.minPrice), " - "), utils.formatDollarAmount(service.cost.maxPrice)]
          })]
        });
    }
  };
  const getSubtitlePrice = () => {
    switch (service.cost.pricingType) {
      case ServiceCostType.Fixed:
      case ServiceCostType.Variable:
        return utils.formatDollarAmount(utils.applyDiscount(service.cost.price, service.cost.discount));
      case ServiceCostType.Range:
        {
          const minFormatted = service.cost.minPrice && !service.cost.minPrice.eq(0) ? "".concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.minPrice, service.cost.discount)), " - ") : 'Up to ';
          return "".concat(minFormatted).concat(utils.formatDollarAmount(utils.applyDiscount(service.cost.maxPrice, service.cost.discount)));
        }
    }
  };
  const hasIncludedServices = ((_service$bundle = service.bundle) === null || _service$bundle === void 0 ? void 0 : _service$bundle.services) && service.bundle.services.length > 0;
  const hasBillingDetails = getBulletPoints('secondary').bullets.length > 0;
  const hasExpandableContent = service.description || hasIncludedServices || hasBillingDetails;
  const parseDescription = description => {
    if (!description) return [];
    const pureText = extractPureTextFromRichText(description);
    return pureText.split('\n').filter(Boolean);
  };
  const getServiceDescription = options => {
    var _service$bundle2;
    const {
      forMobile = false
    } = options !== null && options !== void 0 ? options : {};
    const descriptionLines = parseDescription(service.description);
    const hasDescription = descriptionLines.length > 0;
    const contentSkin = forMobile ? 'standard' : 'secondary';
    const bulletPoints = getBulletPoints(contentSkin);
    return /*#__PURE__*/_jsxs(Column, {
      gap: 16,
      children: [hasDescription && /*#__PURE__*/_jsxs(Column, {
        gap: 4,
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          weight: "bold",
          skin: "standard",
          children: hasIncludedServices ? 'Bundle details' : 'Service details'
        }), descriptionLines.map((text, i) => /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: contentSkin,
          weight: "light",
          children: text
        }, i))]
      }), hasIncludedServices && /*#__PURE__*/_jsxs(Column, {
        gap: 4,
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          weight: "bold",
          skin: "standard",
          children: "Included services"
        }), /*#__PURE__*/_jsx("ul", {
          className: styles.detailsList,
          children: (_service$bundle2 = service.bundle) === null || _service$bundle2 === void 0 ? void 0 : _service$bundle2.services.map(bundleService => /*#__PURE__*/_jsx("li", {
            children: /*#__PURE__*/_jsx(Typography, {
              variant: "paragraph2",
              skin: contentSkin,
              weight: "light",
              children: bundleService.name
            })
          }, bundleService.serviceTemplateId))
        })]
      }), hasBillingDetails && /*#__PURE__*/_jsxs(Column, {
        gap: 4,
        "data-testid": ServiceItemTestIds.BillingDetails(),
        children: [/*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          weight: "bold",
          skin: "standard",
          children: "Billing"
        }), bulletPoints.bullets.map((bullet, i) => typeof bullet === 'string' ? /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph2",
          skin: contentSkin,
          weight: "light",
          children: bullet
        }, i) : /*#__PURE__*/_jsx(Box, {
          children: bullet
        }, i))]
      })]
    });
  };
  const sharedProps = {
    service,
    isOptional,
    isSelected,
    onToggle,
    isDisabled: !!isDisabled,
    discountBadgeData,
    getBillingBadgeText,
    futureBillingText,
    renderPrice,
    getSubtitlePrice,
    hasIncludedServices: !!hasIncludedServices,
    hasExpandableContent: !!hasExpandableContent,
    getServiceDescription,
    ServiceItemTestIds
  };
  if (isMobile) {
    return /*#__PURE__*/_jsx(MobileServiceItem, _objectSpread({}, sharedProps));
  }
  return /*#__PURE__*/_jsx(DesktopServiceItem, _objectSpread({}, sharedProps));
};